import {PageHeading} from '@/components/ui/page-atoms/PageAtoms'
import {AlertTriangleIcon} from '@/components/ui/icon/Icon'
import {routes} from '@/utilities/constants/routes'
import {StyledNameCell, StyledUsersWrapper} from '@/pages/users/style'
import {useGetOperativeUsers} from '@/features/users/queries/useGetOperativeUsers.ts'
import {useGetTherapistRole} from '@/features/users/queries/useGetTherapistRole.ts'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {Avatar} from '@components/ui/avatar/Avatar.tsx'
import i18n, {useTranslation} from '@/translations/i18n.tsx'
import {formatLocaleDate} from '@/dayjs.ts'
import {ChangeEvent, useRef} from 'react'
import {generatePath, useNavigate} from 'react-router-dom'
import {ErrorBox} from '@components/commons/error-box/ErrorBox.tsx'
import {Table, TableProps} from '@components/commons/table/Table.tsx'
import {RowMenu} from '@/features/users/components/row-menu/RowMenu.tsx'
import {hasFullUsersAccess, operativeUsersColumnToLabel, useUsersSearchParams} from '@/features/users/utils.ts'
import {Helmet} from 'react-helmet'
import {
    columnVisibilitySearchParamsToPayload,
    columnVisibilityToSearchParams,
    debounce,
    filtersParamToPayload,
    sortParamToPayload,
    workSectorToLabel
} from '@utilities/helpers.ts'
import {Button} from '@components/ui/button/Button.tsx'
import {ColumnVisibilityMenu} from '@components/commons/columns-visibility-menu/ColumnVisibilityMenu.tsx'
import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {TableBodyCell, TableBodyCellText} from '@components/ui/table-atoms/TableAtoms.tsx'
import {InvitationStatusIcon} from '@/features/users/components/invitation-status-icon/InvitationStatusIcon.tsx'
import {FiltersBar} from '@components/ui/filters-bar/FiltersBar.tsx'
import {TableInputSearch} from '@components/commons/table-input-search/TableInputSearch.tsx'
import {TableBodyRow} from '@components/ui/table-body-row/TableBodyRow.tsx'
import {MoreActionsCell} from '@components/ui/more-actions-cell/MoreActionsCell.tsx'
import {OperativeUser, OperativeUsersColumn, UserBase} from '@/features/users/types'
import {TableFilters} from '@components/commons/table-filters/TableFilters.tsx'
import {SortingMenu} from '@/components/commons/sorting-menu/SortingMenu'
import {WorkAreasBadges} from '@/features/users/components/work-areas-badges/WorkAreasBadges.tsx'
import {useAuthStore, usePermissions} from '@/features/auth/store/store'

// `workAreas` is intentionally not sortable: the backend has no ORDER BY for the relation
const makeSortableColumnsMap = () =>
    OperativeUsersColumn.exclude(['workAreas', 'moreActions']).options.map(column => ({
        id: column,
        label: i18n.t(operativeUsersColumnToLabel[column])
    }))

const makeVisibleColumnsMap = () =>
    OperativeUsersColumn.exclude(['moreActions']).options.map(column => ({
        id: column,
        label: i18n.t(operativeUsersColumnToLabel[column])
    }))

export const OperationalUsers = () => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const roleName = useAuthStore(state => state.user?.roleName)
    /* Only Admin/Amministrazione can open a user's details or act on it: other roles get a plain,
    non-clickable row without the row actions menu */
    const hasFullAccess = !!permissions?.users?.read && hasFullUsersAccess(roleName)
    const navigate = useNavigate()
    const sortableColumnsMap = makeSortableColumnsMap()
    const visibleColumnsMap = makeVisibleColumnsMap()
    const {searchParams, setSearchParams} = useUsersSearchParams()
    const inputSearchRef = useRef<HTMLInputElement | null>(null)
    const role = useGetTherapistRole()

    /* Safety invariants of this list: the therapist role is always the first field and the
    operator is always `and`, so the user filters can only narrow the fixed scope. The `roleId`
    field never reaches the URL — only the user filters live in `searchParams.filters`. */
    const payload: NonNullable<Parameters<typeof useGetOperativeUsers>[1]> = {
        filters: {
            operator: 'and',
            fields: [
                {key: 'roleId', operator: 'in', value: role.roleId ? [role.roleId] : []},
                ...(filtersParamToPayload(searchParams.filters)?.filters?.fields ?? [])
            ]
        },
        sorts: sortParamToPayload(searchParams.sorts)
    }
    const usersQuery = useGetOperativeUsers(
        {
            limit: searchParams.limit,
            page: searchParams.page,
            search: searchParams.search,
            responseType: 'index'
        },
        payload,
        {enabled: !!role.roleId}
    )

    const onSearch = debounce((event: ChangeEvent<HTMLInputElement>) => {
        setSearchParams({search: event.target.value || undefined, page: 1})
    }, 1000)

    const columnVisibility =
        columnVisibilitySearchParamsToPayload(OperativeUsersColumn, searchParams.columnVisibility) ??
        Object.fromEntries(OperativeUsersColumn.options.map(column => [column, true]))

    return (
        <StyledUsersWrapper>
            <Helmet title={t('users:operational_users_meta_title')} />
            <PageHeading justify="space-between">
                <h2>{t('users:operational_users_title')}</h2>
            </PageHeading>
            <FiltersBar>
                <Flexbox align="center" gap={2}>
                    <TableInputSearch
                        ref={inputSearchRef}
                        defaultValue={searchParams.search}
                        onChange={onSearch}
                        placeholder={t('commons:search_name')}
                    />
                </Flexbox>
                <Flexbox style={{flexShrink: 0}} align="center" gap={2}>
                    <TableFilters
                        onApply={filters => setSearchParams({filters: JSON.stringify(filters), page: 1})}
                        filtersParams={searchParams.filters}
                        entity="users"
                        isOperatorLocked
                        availableFilters={[
                            {key: OperativeUsersColumn.enum.sector, label: operativeUsersColumnToLabel.sector},
                            {key: OperativeUsersColumn.enum.birthDate, label: operativeUsersColumnToLabel.birthDate},
                            {key: OperativeUsersColumn.enum.createdAt, label: operativeUsersColumnToLabel.createdAt},
                            {
                                key: OperativeUsersColumn.enum.lastLoginAt,
                                label: operativeUsersColumnToLabel.lastLoginAt
                            },
                            {
                                key: OperativeUsersColumn.enum.specializationId,
                                label: operativeUsersColumnToLabel.specializationId
                            },
                            {key: OperativeUsersColumn.enum.workAreas, label: operativeUsersColumnToLabel.workAreas},
                            {
                                key: OperativeUsersColumn.enum.contactEmail,
                                label: operativeUsersColumnToLabel.contactEmail
                            },
                            {
                                key: OperativeUsersColumn.enum.mobilePhone,
                                label: operativeUsersColumnToLabel.mobilePhone
                            }
                        ]}
                    />
                    <SortingMenu
                        columns={sortableColumnsMap}
                        order={sortParamToPayload(searchParams.sorts) ?? []}
                        setOrder={sorts => setSearchParams({sorts: JSON.stringify(sorts)})}
                    />
                    <ColumnVisibilityMenu
                        columns={visibleColumnsMap}
                        columnVisibility={columnVisibility}
                        onSubmit={columnVisibility =>
                            setSearchParams({columnVisibility: columnVisibilityToSearchParams(columnVisibility)})
                        }
                    />
                </Flexbox>
            </FiltersBar>
            {role.isError || usersQuery.isError ? (
                <ErrorBox title={t('errors:default')} icon={<AlertTriangleIcon />} />
            ) : role.isNotFound ? (
                <EmptyState
                    title={t('users:operational_users_role_missing_title')}
                    subtitle={t('users:operational_users_role_missing_description')}
                />
            ) : (
                <Table
                    emptyStateComponent={
                        <EmptyState
                            title={t('users:no_operational_users_title')}
                            subtitle={t('users:no_operational_users_description')}
                        >
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                    setSearchParams({search: undefined, filters: undefined, page: 1})
                                    if (inputSearchRef.current) {
                                        inputSearchRef.current.value = ''
                                    }
                                }}
                            >
                                {t('users:no_operational_users_cta')}
                            </Button>
                        </EmptyState>
                    }
                    rowComponent={({tableBodyRowProps, dataItem, index}) => (
                        <TableBodyRow
                            // Absolute path: a relative `navigate(id)` would resolve under `/operational-users`
                            onClick={
                                dataItem && hasFullAccess
                                    ? () => navigate(generatePath(routes.USER_DETAILS.handle.path, {id: dataItem.id}))
                                    : undefined
                            }
                            key={index}
                            style={{cursor: dataItem && hasFullAccess ? 'pointer' : 'default'}}
                            {...tableBodyRowProps}
                        />
                    )}
                    pagination={{
                        ...usersQuery.persistedPagination,
                        currentPage: searchParams.page,
                        limit: searchParams.limit,
                        onChangePage: page => setSearchParams({page}),
                        onChangeLimit: limit => setSearchParams({limit, page: 1})
                    }}
                    columnPinning={{
                        left: [OperativeUsersColumn.enum.fullName],
                        right: hasFullAccess ? [OperativeUsersColumn.enum.moreActions] : []
                    }}
                    columnVisibility={columnVisibility}
                    isLoading={role.isPending || usersQuery.isPending}
                    data={usersQuery.data?.data ?? []}
                    columns={[
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <StyledNameCell>
                                        <Avatar
                                            imageUrl={dataItem.avatarUrl ?? undefined}
                                            size="sm"
                                            name={dataItem.fullName}
                                        />
                                        <TableBodyCellText>{dataItem.fullName}</TableBodyCellText>
                                        {dataItem.roleName && (
                                            <InvitationStatusIcon hasAcceptedInvitation={!!dataItem.lastLoginAt} />
                                        )}
                                    </StyledNameCell>
                                </TableBodyCell>
                            ),
                            header: () => t(operativeUsersColumnToLabel.fullName),
                            id: OperativeUsersColumn.enum.fullName,
                            minSize: () => 80,
                            size: () => 250,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{dataItem.specializationName}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(operativeUsersColumnToLabel.specializationId),
                            id: OperativeUsersColumn.enum.specializationId,
                            minSize: () => 80,
                            size: () => 140,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    {dataItem.workSector ? (
                                        <TableBodyCellText>
                                            {t(workSectorToLabel[dataItem.workSector])}
                                        </TableBodyCellText>
                                    ) : (
                                        <TableBodyCellText variant="light">{t('commons:none')}</TableBodyCellText>
                                    )}
                                </TableBodyCell>
                            ),
                            header: () => t(operativeUsersColumnToLabel.sector),
                            id: OperativeUsersColumn.enum.sector,
                            minSize: () => 140,
                            size: () => 140,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <WorkAreasBadges workAreas={dataItem.workAreas} />
                                </TableBodyCell>
                            ),
                            header: () => t(operativeUsersColumnToLabel.workAreas),
                            id: OperativeUsersColumn.enum.workAreas,
                            minSize: () => 160,
                            size: () => 240,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    {dataItem.lastLoginAt ? (
                                        <TableBodyCellText>
                                            {formatLocaleDate(dataItem.lastLoginAt, 'll')}
                                        </TableBodyCellText>
                                    ) : (
                                        <TableBodyCellText variant="light">{t('commons:none')}</TableBodyCellText>
                                    )}
                                </TableBodyCell>
                            ),
                            header: () => t(operativeUsersColumnToLabel.lastLoginAt),
                            id: OperativeUsersColumn.enum.lastLoginAt,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{formatLocaleDate(dataItem.createdAt, 'll')}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(operativeUsersColumnToLabel.createdAt),
                            id: OperativeUsersColumn.enum.createdAt,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{dataItem.email}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(operativeUsersColumnToLabel.contactEmail),
                            id: OperativeUsersColumn.enum.contactEmail,
                            minSize: () => 80,
                            size: () => 200,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{formatLocaleDate(dataItem.birthDate, 'll')}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(operativeUsersColumnToLabel.birthDate),
                            id: OperativeUsersColumn.enum.birthDate,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{dataItem.phoneNumber}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(operativeUsersColumnToLabel.mobilePhone),
                            id: OperativeUsersColumn.enum.mobilePhone,
                            size: () => 160,
                            maxSize: () => 400
                        },
                        ...(hasFullAccess
                            ? [
                                  {
                                      cell: ({dataItem, forwardedProps}) => (
                                          <MoreActionsCell {...forwardedProps}>
                                              <RowMenu
                                                  hasExport={false}
                                                  deleteRedirectTo={routes.OPERATIONAL_USERS_ROUTE.handle.path}
                                                  user={{
                                                      roleName: dataItem.roleName,
                                                      ...UserBase.parse(dataItem)
                                                  }}
                                              />
                                          </MoreActionsCell>
                                      ),
                                      header: () => undefined,
                                      id: OperativeUsersColumn.enum.moreActions,
                                      size: () => 56
                                  } satisfies TableProps<OperativeUser[], string>['columns'][number]
                              ]
                            : [])
                    ]}
                />
            )}
        </StyledUsersWrapper>
    )
}
